import React from "react";

export const renderMarkdown = (text: string) => {
  // Pre-process: Split lines that have --- at the end into separate lines
  text = text.replace(/([^\n])---/g, (_, char) => char + "\n---");
  
  const lines = text.split("\n");
  const elements: JSX.Element[] = [];
  let key = 0;
  let inCodeBlock = false;
  let codeBlockContent: string[] = [];
  let currentOrderedList: JSX.Element[] = [];
  let currentUnorderedList: JSX.Element[] = [];

  const flushOrderedList = () => {
    if (currentOrderedList.length > 0) {
      elements.push(
        <ol key={`ol-${key++}`} className="list-decimal ml-6 space-y-1">
          {currentOrderedList}
        </ol>
      );
      currentOrderedList = [];
    }
  };

  const flushUnorderedList = () => {
    if (currentUnorderedList.length > 0) {
      elements.push(
        <ul key={`ul-${key++}`} className="list-disc ml-6 space-y-1">
          {currentUnorderedList}
        </ul>
      );
      currentUnorderedList = [];
    }
  };

  const flushCodeBlock = () => {
    if (inCodeBlock && codeBlockContent.length > 0) {
      elements.push(
        <pre key={`code-${key++}`} className="bg-muted rounded p-3 text-xs overflow-x-auto my-2">
          <code>{codeBlockContent.join("\n")}</code>
        </pre>
      );
      inCodeBlock = false;
      codeBlockContent = [];
    }
  };

  lines.forEach((line, index) => {
    // Handle code blocks
    if (line.startsWith("```")) {
      flushOrderedList();
      flushUnorderedList();
      if (!inCodeBlock) {
        inCodeBlock = true;
        codeBlockContent = [];
      } else {
        // End of code block
        elements.push(
          <pre key={`code-${key++}`} className="bg-muted rounded p-3 text-xs overflow-x-auto my-2">
            <code>{codeBlockContent.join("\n")}</code>
          </pre>
        );
        inCodeBlock = false;
        codeBlockContent = [];
      }
    } else if (inCodeBlock) {
      codeBlockContent.push(line);
    }
    // Handle ordered list items
    else if (line.match(/^\d+\.\s+/)) {
      flushUnorderedList(); // Close any open unordered list
      const content = line.replace(/^\d+\.\s+/, "");
      currentOrderedList.push(
        <li key={`li-${key++}`} className="text-sm">
          {renderInlineMarkdown(content)}
        </li>
      );
    }
    // Handle horizontal rule (must be checked before unordered lists)
    else if (line.trim() === "---" || line.trim() === "***" || line.trim() === "___" || 
             line.match(/^-{3,}$/) || line.match(/^\*{3,}$/) || line.match(/^_{3,}$/)) {
      flushOrderedList();
      flushUnorderedList();
      elements.push(
        <hr key={`hr-${key++}`} className="my-3 border-t border-border" />
      );
    }
    // Handle unordered list items (bullet points)
    else if (line.match(/^[-*]\s+/)) {
      flushOrderedList(); // Close any open ordered list
      const content = line.replace(/^[-*]\s+/, "");
      currentUnorderedList.push(
        <li key={`li-${key++}`} className="text-sm">
          {renderInlineMarkdown(content)}
        </li>
      );
    }
    // Handle headers (must start at line beginning, optional leading whitespace)
    else if (line.match(/^\s*####\s+/)) {
      flushOrderedList();
      flushUnorderedList();
      const content = line.replace(/^\s*####\s+/, "");
      elements.push(
        <h4 key={`h4-${key++}`} className="text-sm font-medium mt-2 mb-1">
          {renderInlineMarkdown(content)}
        </h4>
      );
    }
    else if (line.match(/^\s*###\s+/)) {
      flushOrderedList();
      flushUnorderedList();
      const content = line.replace(/^\s*###\s+/, "");
      elements.push(
        <h3 key={`h3-${key++}`} className="text-sm font-semibold mt-2 mb-1">
          {renderInlineMarkdown(content)}
        </h3>
      );
    }
    else if (line.match(/^\s*##\s+/)) {
      flushOrderedList();
      flushUnorderedList();
      const content = line.replace(/^\s*##\s+/, "");
      elements.push(
        <h2 key={`h2-${key++}`} className="text-sm font-semibold mt-3 mb-1">
          {renderInlineMarkdown(content)}
        </h2>
      );
    }
    else if (line.match(/^\s*#\s+/) && !line.match(/^\s*##/)) {
      flushOrderedList();
      flushUnorderedList();
      const content = line.replace(/^\s*#\s+/, "");
      elements.push(
        <h1 key={`h1-${key++}`} className="text-base font-bold mt-4 mb-2">
          {renderInlineMarkdown(content)}
        </h1>
      );
    }
    // Regular paragraph
    else if (line.trim()) {
      flushOrderedList();
      flushUnorderedList();
      elements.push(
        <p key={`p-${key++}`} className="text-sm">
          {renderInlineMarkdown(line)}
        </p>
      );
    }
    // Empty line
    else if (index < lines.length - 1) {
      flushOrderedList();
      flushUnorderedList();
      elements.push(<div key={`br-${key++}`} className="h-2" />);
    }
  });

  // Flush any remaining lists and code blocks at the end
  flushOrderedList();
  flushUnorderedList();
  flushCodeBlock();

  return elements;
};

export const renderInlineMarkdown = (text: string) => {
  const parts: (string | JSX.Element)[] = [];
  let key = 0;

  // Process code blocks first
  const codeRegex = /`([^`]+)`/g;
  let lastIndex = 0;
  let match;

  while ((match = codeRegex.exec(text)) !== null) {
    // Add text before code
    if (match.index > lastIndex) {
      const beforeText = text.substring(lastIndex, match.index);
      parts.push(...processBoldItalic(beforeText, key));
      key += 100; // Increment key to avoid conflicts
    }

    // Add code
    parts.push(
      <code key={`code-${key++}`} className="bg-muted px-1 py-0.5 rounded text-xs">
        {match[1]}
      </code>
    );

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    const remainingText = text.substring(lastIndex);
    parts.push(...processBoldItalic(remainingText, key));
  }

  return parts.length > 0 ? parts : text;
};

const processBoldItalic = (text: string, startKey: number) => {
  const parts: (string | JSX.Element)[] = [];
  let key = startKey;

  // Process bold (**text**) and italic (*text*) - check bold first
  const combinedRegex = /(\*\*([^*]+)\*\*|\*([^*]+)\*)/g;
  let lastIndex = 0;
  let match;

  while ((match = combinedRegex.exec(text)) !== null) {
    // Add text before match
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    // Check if it's bold or italic
    if (match[0].startsWith("**")) {
      // Bold text
      parts.push(
        <strong key={`bold-${key++}`} className="font-semibold">
          {match[2]}
        </strong>
      );
    } else {
      // Italic text
      parts.push(
        <em key={`italic-${key++}`} className="italic">
          {match[3]}
        </em>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
};

export const renderMessageWithMentions = (text: string) => {
  // Match AGENT[Name] format in the text
  const mentionRegex = /AGENT\[([^\]]+)\]/g;
  const parts: (string | JSX.Element)[] = [];
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = mentionRegex.exec(text)) !== null) {
    // Add text before the mention
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    // Add the mention as a styled span
    const agentName = match[1];
    parts.push(
      <span 
        key={`mention-${key++}`}
        className="inline-flex items-center bg-white/20 text-white px-2 py-0.5 rounded-md text-sm font-medium"
      >
        @{agentName}
      </span>
    );

    lastIndex = match.index + match[0].length;
  }

  // Add any remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : text;
};

export const stripRedlineTags = (text: any) => {
  if (!text && text !== 0) return text; // Handle 0 as a valid value
  
  // Convert to string if it's not already
  let textStr = typeof text === "string" ? text : String(text);
  
  // Check if this is a JSON string and pretty print it
  if ((textStr.startsWith("{") || textStr.startsWith("[")) && 
      (textStr.endsWith("}") || textStr.endsWith("]"))) {
    try {
      const parsed = JSON.parse(textStr);
      textStr = JSON.stringify(parsed, null, 2);
    } catch (e) {
      // Not valid JSON, continue with regular processing
    }
  }
  
  return textStr.replace(/<REDLINE>|<\/REDLINE>/g, "");
};
